import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package2, ArrowLeft, Shield, Mail, Database, UserCheck, Globe, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PrintButton from '@/components/PrintButton';

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Package2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-heading font-bold">Even Rounds</span>
            </Link>
            <div className="flex items-center gap-4">
              <PrintButton documentTitle={isEnglish ? 'Even Rounds - Privacy Policy' : 'Even Rounds - Tietosuojaseloste'} />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => i18n.changeLanguage(isEnglish ? 'fi' : 'en')}
              >
                <Globe className="h-4 w-4 mr-1" />
                {isEnglish ? 'FI' : 'EN'}
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12" role="main" aria-labelledby="privacy-title">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-10 w-10 text-primary" aria-hidden="true" />
            <h1 id="privacy-title" className="text-4xl font-heading font-bold">
              {isEnglish ? 'Privacy Policy' : 'Tietosuojaseloste'}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {isEnglish ? 'Last updated: December 2025' : 'Viimeksi päivitetty: Joulukuu 2025'}
          </p>
        </div>

        <div className="prose prose-stone max-w-none" role="article">
          {isEnglish ? (
            <>
              <section className="mb-8" aria-labelledby="section-1">
                <h2 id="section-1" className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Database className="h-6 w-6 text-primary" aria-hidden="true" />
                  1. Data Controller
                </h2>
                <p>
                  <strong>Vera Value Oy</strong><br />
                  Business ID (Y-tunnus): 3323295-9<br />
                  VAT Number: FI33232959<br />
                  Mäkelä, Finland<br />
                  Email: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <UserCheck className="h-6 w-6 text-primary" />
                  2. Data We Collect
                </h2>
                <p>We collect the following types of personal data:</p>
                <ul>
                  <li><strong>Account Information:</strong> Name, email address (via Google Sign-In)</li>
                  <li><strong>User Content:</strong> Yarn inventory data, project information, patterns you create</li>
                  <li><strong>Transaction Data:</strong> Purchase history for digital designs, donation records</li>
                  <li><strong>Communication Data:</strong> Messages sent through our platform, newsletter subscriptions</li>
                  <li><strong>Technical Data:</strong> IP address, browser type, device information, cookies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">3. Legal Basis for Processing (GDPR Art. 6)</h2>
                <ul>
                  <li><strong>Contract Performance:</strong> To provide our services to you</li>
                  <li><strong>Legitimate Interest:</strong> To improve our services, prevent fraud</li>
                  <li><strong>Consent:</strong> For marketing emails and non-essential cookies</li>
                  <li><strong>Legal Obligation:</strong> Tax records, legal compliance</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">4. How We Use Your Data</h2>
                <ul>
                  <li>Providing and maintaining our yarn inventory management service</li>
                  <li>Processing payments for digital designs</li>
                  <li>Sending transactional emails (purchase confirmations, comment notifications)</li>
                  <li>Sending marketing communications (with your consent)</li>
                  <li>Analyzing usage patterns to improve our service</li>
                  <li>Preventing fraud and ensuring security</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">5. Data Sharing</h2>
                <p>We share your data with:</p>
                <ul>
                  <li><strong>Payment Processors:</strong> Stripe (for secure payment processing)</li>
                  <li><strong>Email Services:</strong> Resend (for transactional and marketing emails)</li>
                  <li><strong>Authentication:</strong> Google (for sign-in services)</li>
                  <li><strong>Hosting:</strong> Cloud infrastructure providers within EU/EEA</li>
                </ul>
                <p>We do not sell your personal data to third parties.</p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Clock className="h-6 w-6 text-primary" />
                  6. Data Retention
                </h2>
                <ul>
                  <li>Account data: Until you delete your account</li>
                  <li>Transaction records: 6 years (Finnish tax law)</li>
                  <li>Marketing consent: Until withdrawn</li>
                  <li>Technical logs: 90 days</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">7. Your Rights (GDPR Art. 15-22)</h2>
                <p>You have the right to:</p>
                <ul>
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Rectification:</strong> Correct inaccurate data</li>
                  <li><strong>Erasure:</strong> Request deletion of your data (&quot;right to be forgotten&quot;)</li>
                  <li><strong>Restriction:</strong> Limit how we process your data</li>
                  <li><strong>Portability:</strong> Receive your data in a portable format</li>
                  <li><strong>Objection:</strong> Object to processing based on legitimate interest</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
                </ul>
                <p>To exercise these rights, contact us at <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a></p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">8. International Transfers</h2>
                <p>
                  Your data is primarily stored within the EU/EEA. When transfers outside the EEA are necessary 
                  (e.g., to US-based service providers), we ensure appropriate safeguards such as Standard 
                  Contractual Clauses are in place.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">9. Security</h2>
                <p>
                  We implement appropriate technical and organizational measures to protect your data, including:
                </p>
                <ul>
                  <li>Encryption of data in transit (HTTPS/TLS)</li>
                  <li>Secure authentication via Google OAuth</li>
                  <li>Rate limiting to prevent abuse</li>
                  <li>Regular security audits</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">10. Complaints</h2>
                <p>
                  If you believe we have violated your data protection rights, you have the right to lodge a 
                  complaint with the Finnish Data Protection Ombudsman (Tietosuojavaltuutetun toimisto):
                </p>
                <p>
                  Website: <a href="https://tietosuoja.fi/en" target="_blank" rel="noopener noreferrer">tietosuoja.fi</a><br />
                  Email: tietosuoja@om.fi
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Mail className="h-6 w-6 text-primary" />
                  11. Contact Us
                </h2>
                <p>
                  For any privacy-related questions or requests:<br />
                  Email: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
              </section>
            </>
          ) : (
            <>
              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Database className="h-6 w-6 text-primary" />
                  1. Rekisterinpitäjä
                </h2>
                <p>
                  <strong>Vera Value Oy</strong><br />
                  Y-tunnus: 3323295-9<br />
                  ALV-numero: FI33232959<br />
                  Mäkelä, Suomi<br />
                  Sähköposti: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <UserCheck className="h-6 w-6 text-primary" />
                  2. Kerättävät tiedot
                </h2>
                <p>Keräämme seuraavia henkilötietoja:</p>
                <ul>
                  <li><strong>Tilitiedot:</strong> Nimi, sähköpostiosoite (Google-kirjautumisen kautta)</li>
                  <li><strong>Käyttäjäsisältö:</strong> Lankavarastotiedot, projektitiedot, luomasi ohjeet</li>
                  <li><strong>Tapahtumatiedot:</strong> Digitaalisten mallien ostohistoria, lahjoitustiedot</li>
                  <li><strong>Viestintätiedot:</strong> Alustan kautta lähetetyt viestit, uutiskirjetilaukset</li>
                  <li><strong>Tekniset tiedot:</strong> IP-osoite, selaintyyppi, laitetiedot, evästeet</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">3. Käsittelyn oikeusperuste (GDPR Art. 6)</h2>
                <ul>
                  <li><strong>Sopimuksen täytäntöönpano:</strong> Palveluidemme tarjoaminen sinulle</li>
                  <li><strong>Oikeutettu etu:</strong> Palveluidemme parantaminen, petosten estäminen</li>
                  <li><strong>Suostumus:</strong> Markkinointisähköpostit ja ei-välttämättömät evästeet</li>
                  <li><strong>Lakisääteinen velvollisuus:</strong> Verotiedot, lakisääteinen noudattaminen</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">4. Tietojen käyttötarkoitus</h2>
                <ul>
                  <li>Lankavaraston hallintapalvelun tarjoaminen ja ylläpito</li>
                  <li>Digitaalisten mallien maksujen käsittely</li>
                  <li>Tapahtumasähköpostien lähettäminen (ostovahvistukset, kommentti-ilmoitukset)</li>
                  <li>Markkinointiviestinnän lähettäminen (suostumuksellasi)</li>
                  <li>Käyttötapojen analysointi palvelun parantamiseksi</li>
                  <li>Petosten estäminen ja turvallisuuden varmistaminen</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">5. Tietojen jakaminen</h2>
                <p>Jaamme tietojasi seuraavien kanssa:</p>
                <ul>
                  <li><strong>Maksunvälittäjät:</strong> Stripe (turvallinen maksunkäsittely)</li>
                  <li><strong>Sähköpostipalvelut:</strong> Resend (tapahtuma- ja markkinointisähköpostit)</li>
                  <li><strong>Tunnistautuminen:</strong> Google (kirjautumispalvelut)</li>
                  <li><strong>Palvelinpalvelut:</strong> Pilvipalveluntarjoajat EU/ETA-alueella</li>
                </ul>
                <p>Emme myy henkilötietojasi kolmansille osapuolille.</p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Clock className="h-6 w-6 text-primary" />
                  6. Tietojen säilytys
                </h2>
                <ul>
                  <li>Tilitiedot: Kunnes poistat tilisi</li>
                  <li>Tapahtumatiedot: 6 vuotta (Suomen verolainsäädäntö)</li>
                  <li>Markkinointisuostumus: Kunnes peruutat sen</li>
                  <li>Tekniset lokit: 90 päivää</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">7. Oikeutesi (GDPR Art. 15-22)</h2>
                <p>Sinulla on oikeus:</p>
                <ul>
                  <li><strong>Pääsy:</strong> Pyytää kopio henkilötiedoistasi</li>
                  <li><strong>Oikaisu:</strong> Korjata virheelliset tiedot</li>
                  <li><strong>Poisto:</strong> Pyytää tietojesi poistamista (&quot;oikeus tulla unohdetuksi&quot;)</li>
                  <li><strong>Rajoitus:</strong> Rajoittaa tietojesi käsittelyä</li>
                  <li><strong>Siirrettävyys:</strong> Saada tietosi siirrettävässä muodossa</li>
                  <li><strong>Vastustaminen:</strong> Vastustaa oikeutettuun etuun perustuvaa käsittelyä</li>
                  <li><strong>Suostumuksen peruuttaminen:</strong> Peruuttaa suostumuksesi milloin tahansa</li>
                </ul>
                <p>Näiden oikeuksien käyttämiseksi ota yhteyttä: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a></p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">8. Kansainväliset siirrot</h2>
                <p>
                  Tietosi säilytetään ensisijaisesti EU/ETA-alueella. Kun siirrot ETA-alueen ulkopuolelle ovat 
                  välttämättömiä (esim. yhdysvaltalaisille palveluntarjoajille), varmistamme asianmukaiset 
                  suojatoimet, kuten vakiosopimuslausekkeet.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">9. Turvallisuus</h2>
                <p>
                  Toteutamme asianmukaiset tekniset ja organisatoriset toimenpiteet tietojesi suojaamiseksi:
                </p>
                <ul>
                  <li>Tietojen salaus siirron aikana (HTTPS/TLS)</li>
                  <li>Turvallinen tunnistautuminen Google OAuth:n kautta</li>
                  <li>Pyyntörajoitukset väärinkäytön estämiseksi</li>
                  <li>Säännölliset tietoturva-auditoinnit</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">10. Valitukset</h2>
                <p>
                  Jos uskot, että olemme rikkoneet tietosuojaoikeuksiasi, sinulla on oikeus tehdä valitus 
                  Tietosuojavaltuutetun toimistolle:
                </p>
                <p>
                  Verkkosivusto: <a href="https://tietosuoja.fi" target="_blank" rel="noopener noreferrer">tietosuoja.fi</a><br />
                  Sähköposti: tietosuoja@om.fi
                </p>
              </section>

              <section className="mb-8">
                <h2 className="flex items-center gap-2 text-2xl font-heading font-semibold">
                  <Mail className="h-6 w-6 text-primary" />
                  11. Yhteystiedot
                </h2>
                <p>
                  Tietosuojaa koskevissa kysymyksissä tai pyynnöissä:<br />
                  Sähköposti: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
              </section>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Vera Value Oy. {isEnglish ? 'All rights reserved.' : 'Kaikki oikeudet pidätetään.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
